import { selectCurrentTemplates } from 'data/Templates/selectors'
import { IPostShiftFormValues } from '@features/shift-post-v2/pages/post-shift/post-shift.form'
import { URLManager } from '@src/helpers/URLManager'
import { ITemplate } from 'models/Template'
import { FormikProps } from 'formik'
import { useCallback, useEffect } from 'react'
import { useSelector } from 'react-redux'

/**
 * Hook to handle the template selection and update the formik values
 */
export const useTemplate = (
  formik: FormikProps<IPostShiftFormValues>,
  isTemplateSection?: boolean
) => {
  const { setFieldValue } = formik
  const templates = useSelector(selectCurrentTemplates)

  /**
   * Updates the formik values with the selected template data
   */
  const updateFormikWithTemplateData = useCallback(
    (selectedTemplate: ITemplate) => {
      setFieldValue('template_id', selectedTemplate.id)
      setFieldValue('gig_type', selectedTemplate.gig_type?.name ?? '')
      setFieldValue('gig_type_id', selectedTemplate.gig_type_id)
      setFieldValue('description_short', selectedTemplate.description_short)
      setFieldValue('description_rich_text', selectedTemplate.description_rich_text)
      setFieldValue(
        'special_instructions_rich_text',
        selectedTemplate.special_instructions_rich_text
      )
      setFieldValue('what_to_wear_rich_text', selectedTemplate.what_to_wear_rich_text)
      setFieldValue('travel_tips_rich_text', selectedTemplate.travel_tips_rich_text)
      setFieldValue('training_mode', selectedTemplate.training_mode)
      setFieldValue('min_age', selectedTemplate.min_age)
      setFieldValue('amount_lifted_frequently', selectedTemplate.amount_lifted_frequently)
      // If the frequently lifted weight is 50, set the occasionally lifted weight to 50
      const maxLiftingWeight = 50
      const isLiftedFrequentlyMax = selectedTemplate.amount_lifted_frequently === maxLiftingWeight

      const amountLiftedOccasionally = isLiftedFrequentlyMax
        ? maxLiftingWeight
        : selectedTemplate.amount_lifted_occasionally
      setFieldValue('amount_lifted_occasionally', amountLiftedOccasionally)
      setFieldValue('amount_of_time_spent_on_feet', selectedTemplate.amount_of_time_spent_on_feet)

      // Set pay_value from template
      // Else, would remain 15 from the initial values definition
      if (selectedTemplate.pay_value) {
        setFieldValue('pay_value', selectedTemplate.pay_value)
      }

      // Map acceptance criteria fields from template
      setFieldValue('auto_accept', selectedTemplate.auto_accept)
      setFieldValue('auto_accept_by_certificates', selectedTemplate.auto_accept_by_certificates)
      setFieldValue('auto_accept_by_worked_before', selectedTemplate.auto_accept_by_worked_before)
      setFieldValue('auto_accept_by_favored', selectedTemplate.auto_accept_by_favored)

      if (selectedTemplate.auto_accept_certificates) {
        const ids = selectedTemplate.auto_accept_certificates.data.map(
          (certificate) => certificate.id
        )
        setFieldValue('auto_accept_certificate_ids', ids)
      }
    },
    [setFieldValue]
  )

  useEffect(() => {
    // Disable this effect for the template section
    if (!isTemplateSection) {
      const urlManager = new URLManager()
      const templateIdParam = urlManager.getParam('template_id')

      if (templateIdParam && templates.some((t) => t.id === Number(templateIdParam))) {
        const template = templates.find((t) => t.id === Number(templateIdParam))
        if (template) {
          updateFormikWithTemplateData(template)
        }
      }
    }
  }, [isTemplateSection, templates, updateFormikWithTemplateData])

  return {
    updateFormikWithTemplateData,
  }
}
